import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Copy, UserCog, X, ListChecks, LayoutTemplate, Trash2 } from 'lucide-react';
import { usePermissions } from '@/contexts/PermissionsContext';

export type BulkAction = 'change-status' | 'change-manager' | 'duplicate' | 'save-as-template' | 'delete';

interface BulkActionsBarProps {
  selectedCount: number;
  onAction: (action: BulkAction) => void;
  onClear: () => void;
}

export function BulkActionsBar({
  selectedCount,
  onAction,
  onClear,
}: BulkActionsBarProps) {
  const { canPerformAction } = usePermissions();
  if (selectedCount === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        borderRadius: '9999px',
        backgroundColor: 'var(--primary)',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        whiteSpace: 'nowrap',
        opacity: 1,
      }}
    >
      {/* Count badge */}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '9999px',
          backgroundColor: 'rgba(255,255,255,0.25)',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '12px',
          minWidth: '22px',
          height: '22px',
          padding: '0 6px',
        }}
      >
        {selectedCount}
      </span>

      <span>{selectedCount === 1 ? 'project selected' : 'projects selected'}</span>

      {/* Divider */}
      <span style={{ width: '1px', height: '16px', backgroundColor: 'rgba(255,255,255,0.4)' }} />

      {/* Actions dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            style={{ color: '#ffffff', height: '28px', padding: '0 8px', gap: '4px' }}
            className="hover:bg-white/20 hover:text-white focus-visible:ring-white/40"
          >
            <ListChecks style={{ width: '14px', height: '14px' }} />
            Actions
            <ChevronDown style={{ width: '14px', height: '14px' }} />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="center" className="w-52">
          {canPerformAction('changeStatus') && (
            <DropdownMenuItem className="gap-2" onSelect={() => onAction('change-status')}>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-muted text-muted-foreground">
                <ListChecks className="h-3.5 w-3.5" />
              </span>
              Change Status
            </DropdownMenuItem>
          )}

          {canPerformAction('assignKeyPeople') && (
            <DropdownMenuItem className="gap-2" onSelect={() => onAction('change-manager')}>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-muted text-muted-foreground">
                <UserCog className="h-3.5 w-3.5" />
              </span>
              Change Project Manager
            </DropdownMenuItem>
          )}

          {selectedCount === 1 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2" onSelect={() => onAction('duplicate')}>
                <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-muted text-muted-foreground">
                  <Copy className="h-3.5 w-3.5" />
                </span>
                Duplicate Project
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2" onSelect={() => onAction('save-as-template')}>
                <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-muted text-muted-foreground">
                  <LayoutTemplate className="h-3.5 w-3.5" />
                </span>
                Save as Template
              </DropdownMenuItem>
              {canPerformAction('delete') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="gap-2 text-destructive focus:text-destructive focus:bg-destructive/10"
                    onSelect={() => onAction('delete')}
                  >
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-destructive/10 text-destructive">
                      <Trash2 className="h-3.5 w-3.5" />
                    </span>
                    Delete Project
                  </DropdownMenuItem>
                </>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Divider */}
      <span style={{ width: '1px', height: '16px', backgroundColor: 'rgba(255,255,255,0.4)' }} />

      {/* Clear */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onClear}
        style={{ color: '#ffffff', height: '28px', padding: '0 8px', gap: '4px' }}
        className="hover:bg-white/20 hover:text-white focus-visible:ring-white/40"
      >
        <X style={{ width: '14px', height: '14px' }} />
        Clear
      </Button>
    </div>
  );
}
